import React, { useState, useEffect } from 'react';
import { DocumentArrowDownIcon, CalendarIcon, AdjustmentsHorizontalIcon, ChartBarIcon } from '@heroicons/react/24/outline';

const DensityReportGenerator = ({ 
  projectData, 
  measurements, 
  onGenerateReport,
  isLoading = false 
}) => {
  const [step, setStep] = useState(1);
  const [reportSettings, setReportSettings] = useState({
    compactionMin: 92.0, // This IS the pass threshold
    compactionMax: 98.0,
    compactionEnableMax: false,
    startDate: '',
    endDate: '',
    organizationName: ''
  });

  // Auto-populate organization name and date range
  useEffect(() => {
    if (measurements && measurements.length > 0) {
      const orgName = measurements[0]?.organization_name || '';
      setReportSettings(prev => ({
        ...prev,
        organizationName: orgName
      }));

      const dates = measurements
        .map(m => new Date(m.LocalTimeOfMeasurement))
        .filter(date => !isNaN(date));
      
      if (dates.length > 0) {
        const minDate = new Date(Math.min(...dates));
        const maxDate = new Date(Math.max(...dates));
        
        setReportSettings(prev => ({
          ...prev,
          startDate: minDate.toISOString().split('T')[0],
          endDate: maxDate.toISOString().split('T')[0]
        }));
      }
    }
  }, [measurements]);

  // Filter measurements based on date range
  const getFilteredMeasurements = () => {
    if (!measurements) return [];
    
    let filtered = measurements;
    
    if (reportSettings.startDate) {
      const startDate = new Date(reportSettings.startDate);
      filtered = filtered.filter(m => new Date(m.LocalTimeOfMeasurement) >= startDate);
    }
    
    if (reportSettings.endDate) {
      const endDate = new Date(reportSettings.endDate + 'T23:59:59');
      filtered = filtered.filter(m => new Date(m.LocalTimeOfMeasurement) <= endDate);
    }
    
    return filtered;
  };

  const filteredMeasurements = getFilteredMeasurements();

  // Quality status based only on compaction
  const getQualityStatus = (compaction) => {
    if (compaction === null || compaction === undefined || isNaN(compaction)) return 'unknown';
    
    const aboveMin = compaction >= reportSettings.compactionMin;
    const belowMax = !reportSettings.compactionEnableMax || !reportSettings.compactionMax || compaction <= reportSettings.compactionMax;
    
    return (aboveMin && belowMax) ? 'pass' : 'fail';
  };

  // Calculate preview statistics
  const validMeasurements = filteredMeasurements.filter(m => 
    m.Compaction != null && !isNaN(m.Compaction)
  );

  const compactionPass = validMeasurements.filter(m => getQualityStatus(m.Compaction) === 'pass').length;
  const compactionFail = validMeasurements.filter(m => getQualityStatus(m.Compaction) === 'fail').length;
  
  const passRate = validMeasurements.length > 0 ? (compactionPass / validMeasurements.length * 100).toFixed(1) : 0;

  const compactionOutOfRange = validMeasurements.filter(m => {
    const value = m.Compaction;
    const belowMin = value < reportSettings.compactionMin;
    const aboveMax = reportSettings.compactionEnableMax && reportSettings.compactionMax && value > reportSettings.compactionMax;
    return belowMin || aboveMax;
  }).length;

const handleGenerateReport = () => {
    const orgName = filteredMeasurements.length > 0 ? filteredMeasurements[0].organization_name : reportSettings.organizationName;
    
    const options = {
      companyInfo: orgName ? {
        name: orgName,
        address: '',
        phone: '',
        email: ''
      } : null,
      compactionMin: reportSettings.compactionMin,
      compactionMax: reportSettings.compactionMax,
      compactionEnableMax: reportSettings.compactionEnableMax,
      includeCompanyInfo: !!orgName,
      includeOperatorSummary: true,
      includeDetailedMeasurements: true,
      
      // NEW: Add map options
      includeMap: true,
      mapOptions: {
        mapType: 'satellite',  // Options: satellite, roadmap, hybrid, terrain
        size: '800x480'
      }
    };
    
    const dateRange = reportSettings.startDate && reportSettings.endDate 
      ? `${reportSettings.startDate}_to_${reportSettings.endDate}`
      : new Date().toISOString().split('T')[0];
    
    const filename = `${projectData?.project_display_name || 'density-report'}-${dateRange}.pdf`;
    
    onGenerateReport(projectData, filteredMeasurements, filename, options);
  };

  const updateSetting = (key, value) => {
    setReportSettings(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const isValidDateRange = () => {
    if (!reportSettings.startDate || !reportSettings.endDate) return false;
    return new Date(reportSettings.startDate) <= new Date(reportSettings.endDate);
  };

  const canGenerate = isValidDateRange() && filteredMeasurements.length > 0;

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-6">
      {/* Header */}
      <div className="mb-8">
        <h3 className="text-xl font-semibold text-gray-900">Generate Density Report</h3>
        <p className="text-sm text-gray-600 mt-1">
          Create a comprehensive PDF report for asphalt compaction measurements
        </p>
        
        {(reportSettings.organizationName || (filteredMeasurements.length > 0 && filteredMeasurements[0].organization_name)) && (
          <div className="mt-3 inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800">
            {filteredMeasurements.length > 0 ? filteredMeasurements[0].organization_name : reportSettings.organizationName}
          </div>
        )}
      </div>

      {/* Step Progress */}
      <div className="mb-8">
        <div className="flex items-center">
          {[1, 2, 3].map((stepNumber) => (
            <React.Fragment key={stepNumber}>
              <div className={`flex items-center justify-center w-8 h-8 rounded-full text-sm font-medium ${
                step >= stepNumber 
                  ? 'bg-blue-600 text-white' 
                  : 'bg-gray-200 text-gray-500'
              }`}>
                {stepNumber}
              </div>
              {stepNumber < 3 && (
                <div className={`flex-1 h-1 mx-4 ${
                  step > stepNumber ? 'bg-blue-600' : 'bg-gray-200'
                }`} />
              )}
            </React.Fragment>
          ))}
        </div>
        <div className="flex justify-between mt-2 text-sm text-gray-600">
          <span>Date Range</span>
          <span>Compaction Thresholds</span>
          <span>Generate Report</span>
        </div>
      </div>

      {/* Step 1: Date Range Selection */}
      {step === 1 && (
        <div className="space-y-6">
          <div className="flex items-center mb-4">
            <CalendarIcon className="h-5 w-5 text-blue-600 mr-2" />
            <h4 className="text-lg font-medium text-gray-900">Select Date Range</h4>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Start Date</label>
              <input
                type="date"
                value={reportSettings.startDate}
                onChange={(e) => updateSetting('startDate', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">End Date</label>
              <input
                type="date"
                value={reportSettings.endDate}
                onChange={(e) => updateSetting('endDate', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
          </div>

          {/* Date Range Preview */}
          <div className="bg-gray-50 rounded-lg p-4">
            <h5 className="text-sm font-medium text-gray-700 mb-2">Preview</h5>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
              <div>
                <span className="text-gray-500">Measurements:</span>
                <div className="font-semibold text-lg">{filteredMeasurements.length}</div>
              </div>
              <div>
                <span className="text-gray-500">Date Range:</span>
                <div className="font-medium">
                  {filteredMeasurements.length > 0 ? `${Math.ceil((new Date(reportSettings.endDate) - new Date(reportSettings.startDate)) / (1000 * 60 * 60 * 24) + 1)} days` : '0 days'}
                </div>
              </div>
              <div>
                <span className="text-gray-500">Operators:</span>
                <div className="font-medium">
                  {new Set(filteredMeasurements.map(m => m.operator_name || 'Unknown')).size}
                </div>
              </div>
              <div>
                <span className="text-gray-500">Valid Data:</span>
                <div className="font-medium">{validMeasurements.length}</div>
              </div>
            </div>
          </div>

          {!isValidDateRange() && reportSettings.startDate && reportSettings.endDate && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-md">
              <p className="text-sm text-red-600">End date must be after start date</p>
            </div>
          )}

          <div className="flex justify-end">
            <button
              onClick={() => setStep(2)}
              disabled={!isValidDateRange() || filteredMeasurements.length === 0}
              className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Next: Set Thresholds
            </button>
          </div>
        </div>
      )}

      {/* Step 2: Compaction Thresholds */}
      {step === 2 && (
        <div className="space-y-6">
          <div className="flex items-center mb-4">
            <AdjustmentsHorizontalIcon className="h-5 w-5 text-blue-600 mr-2" />
            <h4 className="text-lg font-medium text-gray-900">Compaction Thresholds</h4>
          </div>

          <div className="space-y-4">
            <h5 className="text-md font-medium text-gray-800">Compaction (%)</h5>
            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  <span className="flex items-center">
                    <div className="w-3 h-3 bg-green-500 rounded-full mr-2"></div>
                    Pass Threshold (≥)
                  </span>
                </label>
                <input
                  type="number"
                  step="0.1"
                  value={reportSettings.compactionMin}
                  onChange={(e) => updateSetting('compactionMin', parseFloat(e.target.value) || 0)}
                  className="w-[20vw] px-3 py-2 border border-gray-300 rounded-md focus:ring-green-500 focus:border-green-500"
                />
                <p className="text-xs text-gray-500 mt-1">Values ≥ this number will pass</p>
              </div>
              <div>
                <div className="flex items-center mb-2">
                  <input
                    type="checkbox"
                    checked={reportSettings.compactionEnableMax}
                    onChange={(e) => updateSetting('compactionEnableMax', e.target.checked)}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <label className="ml-2 block text-sm font-medium text-gray-700">
                    Enable Maximum Limit
                  </label>
                </div>
                {reportSettings.compactionEnableMax && (
                  <>
                    <input
                      type="number"
                      step="0.1"
                      value={reportSettings.compactionMax}
                      onChange={(e) => updateSetting('compactionMax', parseFloat(e.target.value) || 0)}
                      className="w-[20vw] px-3 py-2 border border-gray-300 rounded-md focus:ring-gray-500 focus:border-gray-500"
                      placeholder="Maximum value (≤)"
                    />
                    <p className="text-xs text-gray-500 mt-1">Values must be between {reportSettings.compactionMin}% and {reportSettings.compactionMax}% to pass</p>
                  </>
                )}
              </div>
            </div>
          </div>

          {/* Threshold Preview */}
          <div className="bg-gray-50 rounded-lg p-4">
            <h5 className="text-sm font-medium text-gray-700 mb-3">Quality Preview with Current Thresholds</h5>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="text-center">
                <div className="text-2xl font-bold text-green-600">{compactionPass}</div>
                <div className="text-xs text-green-800">Compaction Pass</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-red-600">{compactionFail}</div>
                <div className="text-xs text-red-800">Compaction Fail</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-purple-600">{compactionPass}</div>
                <div className="text-xs text-purple-800">Overall Pass</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-gray-900">{passRate}%</div>
                <div className="text-xs text-gray-600">Pass Rate</div>
              </div>
            </div>
          </div>

          <div className="flex justify-between">
            <button
              onClick={() => setStep(1)}
              className="px-6 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300"
            >
              Back: Date Range
            </button>
            <button
              onClick={() => setStep(3)}
              className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
            >
              Next: Generate Report
            </button>
          </div>
        </div>
      )}

      {/* Step 3: Generate Report */}
      {step === 3 && (
        <div className="space-y-6">
          <div className="flex items-center mb-4">
            <ChartBarIcon className="h-5 w-5 text-blue-600 mr-2" />
            <h4 className="text-lg font-medium text-gray-900">Report Summary</h4>
          </div>

          {/* Final Summary */}
          <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg p-6 border border-blue-200">
            <h5 className="text-lg font-semibold text-gray-900 mb-4">Your Report Will Include:</h5>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
              <div>
                <h6 className="font-medium text-gray-700 mb-2">Project Information</h6>
                <ul className="text-sm text-gray-600 space-y-1">
                  <li>• Project: {projectData?.project_display_name || 'N/A'}</li>
                  <li>• Date Range: {reportSettings.startDate} to {reportSettings.endDate}</li>
                </ul>
              </div>
              
              <div>
                <h6 className="font-medium text-gray-700 mb-2">Compaction Thresholds</h6>
                <ul className="text-sm text-gray-600 space-y-1">
                  <li>• Compaction Pass: ≥{reportSettings.compactionMin}%</li>
                  {reportSettings.compactionEnableMax && (
                    <li>• Compaction Max: ≤{reportSettings.compactionMax}%</li>
                  )}
                </ul>
              </div>
            </div>

            <div className="bg-white rounded-lg p-4 mb-4">
              <h6 className="font-medium text-gray-700 mb-3">Quality Summary</h6>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="text-center">
                  <div className="text-xl font-bold text-green-600">{passRate}%</div>
                  <div className="text-xs text-gray-600">Overall Pass Rate</div>
                </div>
                <div className="text-center">
                  <div className="text-xl font-bold text-blue-600">{validMeasurements.length}</div>
                  <div className="text-xs text-gray-600">Valid Measurements</div>
                </div>
                <div className="text-center">
                  <div className="text-xl font-bold text-purple-600">{new Set(filteredMeasurements.map(m => m.operator_name || 'Unknown')).size}</div>
                  <div className="text-xs text-gray-600">Operators</div>
                </div>
                <div className="text-center">
                  <div className="text-xl font-bold text-orange-600">{compactionPass}</div>
                  <div className="text-xs text-gray-600">Passing Tests</div>
                </div>
              </div>
              
              
            </div>
          </div>

          {/* Generate Button */}
          <div className="flex justify-between">
            <button
              onClick={() => setStep(2)}
              className="px-6 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300"
            >
              Back: Thresholds
            </button>
            
            <button
              onClick={handleGenerateReport}
              disabled={isLoading || !canGenerate}
              className={`inline-flex items-center px-8 py-3 rounded-lg font-medium text-lg transition-all duration-200 ${
                isLoading || !canGenerate
                  ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  : 'bg-blue-600 text-white hover:bg-blue-700 focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 shadow-lg hover:shadow-xl'
              }`}
            >
              {isLoading ? (
                <>
                  <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-gray-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Generating PDF...
                </>
              ) : (
                <>
                  <DocumentArrowDownIcon className="h-5 w-5 mr-2" />
                  Generate PDF Report
                </>
              )}
            </button>
          </div>
        </div>
      )}

      {/* No measurements warning */}
      {!measurements?.length && (
        <div className="mt-6 p-4 bg-yellow-50 border border-yellow-200 rounded-md">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-yellow-400" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-yellow-800">No measurements available</h3>
              <p className="text-sm text-yellow-700 mt-1">
                Please ensure you have measurement data loaded to generate a report.
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DensityReportGenerator;